import type { CSSProperties } from 'react';
import type { DeadlineRow, DeadlineDisplayType } from './types';

// [i18nKey, fallback]. Keyed on the DISPLAY type, so it covers the six type tokens the API sends
// verbatim plus the two states derived below (utvardera_anbud, expired_contract_signing). The
// Lokalise keys are English, matching the rest of the deadlines.type.* subtree. The fallbacks are
// the Swedish source copy — they render until Lokalise serves the reader's language.
export const TYPE_LABELS: Record<DeadlineDisplayType, [string, string]> = {
  anbudsinlamning: ['deadlines.type.bidSubmission', 'Anbudsinlämning'],
  utvardera_anbud: ['deadlines.type.bidEvaluation', 'Utvärdera anbud'],
  aktivitet: ['deadlines.type.activity', 'Aktivitet'],
  inkop_klart: ['deadlines.type.purchaseCompleted', 'Inköp klart'],
  forfragan_skickas_senast: ['deadlines.type.quotationSentBy', 'Förfrågan skickas senast'],
  ramavtal_utgang: ['deadlines.type.frameworkAgreementExpiry', 'Ramavtal utgångstid'],
  pending_contract: ['deadlines.type.pendingContract', 'Pågående signering'],
  expired_contract_signing: ['deadlines.type.expiredContractSigning', 'Signering utgången'],
};

// Display types, not source types: these drive the filter pills, and a user can legitimately
// filter by "Utvärdera anbud" or "Signering utgången" even though no row arrives with that type.
export const ACTIVITY_TYPES = Object.keys(TYPE_LABELS) as DeadlineDisplayType[];

export type ActivityColor = { solid: string; badgeBg: string; badgeText: string };

export const ACTIVITY_COLORS: Record<DeadlineDisplayType, ActivityColor> = {
  anbudsinlamning:          { solid: '#3b82f6', badgeBg: '#eff6ff', badgeText: '#1d4ed8' },
  utvardera_anbud:          { solid: '#8b5cf6', badgeBg: '#f5f3ff', badgeText: '#6d28d9' },
  aktivitet:                { solid: '#06b6d4', badgeBg: '#ecfeff', badgeText: '#0e7490' },
  inkop_klart:              { solid: '#22c55e', badgeBg: '#f0fdf4', badgeText: '#15803d' },
  forfragan_skickas_senast: { solid: '#f59e0b', badgeBg: '#fffbeb', badgeText: '#b45309' },
  ramavtal_utgang:          { solid: '#ef4444', badgeBg: '#fef2f2', badgeText: '#b91c1c' },
  pending_contract:         { solid: '#f97316', badgeBg: '#fff7ed', badgeText: '#c2410c' },
  expired_contract_signing: { solid: '#c2410c', badgeBg: '#fff7ed', badgeText: '#9a3412' },
};

// Feeds the shared .typeDot rule (DeadlinesTab.module.scss): the dot's size and shape live in
// CSS, only its per-type colour comes from here. Falls back to slate for a type this bundle does
// not know — the API owns this vocabulary, so an unmapped value must degrade to a grey dot rather
// than throw during render (the app has no ErrorBoundary). Matches TodoCalendar's colorOf.
export const typeDotStyle = (type: DeadlineDisplayType): CSSProperties =>
  ({ '--dot-color': ACTIVITY_COLORS[type]?.solid ?? '#94a3b8' } as CSSProperties);

// The single place a source type becomes a display type. Everything user-facing — the table badge,
// the type filter, the calendar dot and its label — must go through this, or an overdue row renders
// under its pre-deadline identity.
export const getDisplayType = (row: Pick<DeadlineRow, 'type' | 'daysLeft'>): DeadlineDisplayType =>{
  if (row.type === 'anbudsinlamning' && row.daysLeft < 0) return 'utvardera_anbud';
  // Once the 90-day Scrive window has passed, the same activity is shown as expired —
  // a label change only; the underlying row (and its calendar deadline) is unchanged.
  if (row.type === 'pending_contract' && row.daysLeft < 0) return 'expired_contract_signing';
  return row.type;
};

// A signing past its deadline is a DEAD document — Scrive will not accept a signature on it — so
// unlike every other activity type its age stops meaning urgency and starts meaning archaeology.
export const isDeadSigning = (row: Pick<DeadlineRow, 'type' | 'daysLeft'>): boolean =>
  getDisplayType(row) === 'expired_contract_signing';

// Dead signings sort after everything else. Live rows keep most-overdue-first (more overdue really
// is more urgent); the dead group is ordered most-RECENTLY expired first, since those are the ones
// whose purchase is likely still live and worth re-sending.
//
// Must be applied everywhere rows are ordered — the pre-sorted `data` AND the daysLeft column's
// customSort, which is what actually drives the rendered order via defaultOrderBy.
// Mirrors compareDeadlineRows in src/services/dashboardService.js — keep the two in step.
export const compareDeadlineRows = (
  a: Pick<DeadlineRow, 'type' | 'daysLeft'>,
  b: Pick<DeadlineRow, 'type' | 'daysLeft'>,
): number => {
  const deadA = isDeadSigning(a);
  const deadB = isDeadSigning(b);
  if (deadA !== deadB) return deadA ? 1 : -1;
  return deadA ? b.daysLeft - a.daysLeft : a.daysLeft - b.daysLeft;
};

export type UrgencyTone = 'red' | 'yellow' | 'gray';
export const URGENCY_COLORS: Record<UrgencyTone, { bg: string; text: string }> = {
  red: { bg: 'rgb(254, 226, 226)', text: 'rgb(220, 38, 38)' },
  yellow: { bg: 'rgb(254, 249, 195)', text: 'rgb(161, 98, 7)' },
  gray: { bg: 'rgb(243, 244, 246)', text: 'rgb(75, 85, 99)' },
};
export const urgencyTone = (daysLeft: number): UrgencyTone =>
  daysLeft < 0 ? 'red' : daysLeft <= 7 ? 'yellow' : 'gray';

// Feeds the --tone-bg / --tone-text custom properties consumed by .daysLeftBadge,
// .metricCardNumber and .metricCardIcon (DeadlinesTab.module.scss).
export const urgencyStyle = (tone: UrgencyTone): CSSProperties =>
  ({ '--tone-bg': URGENCY_COLORS[tone].bg, '--tone-text': URGENCY_COLORS[tone].text } as CSSProperties);

const ACTIVITY_ID_PREFIX = 'aktivitet:';

export const taskIdOf = (row: DeadlineRow): string | null =>
  row.type === 'aktivitet' ? row.id.slice(ACTIVITY_ID_PREFIX.length) : null;
